import { resolvedConcentrationDisplay } from './concentrationResolver.js'
import { strictNameMatch } from './collectionEntryMatch.js'

const CONCENTRATION_ORDER = [
  'cologne', 'edc', 'edt', 'edp', 'edp intense', 'le parfum',
  'parfum', 'extrait', 'extrait de parfum', 'elixir', 'perfume oil',
]

function normalized(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

function sameCatalogLine(a, b) {
  if (normalized(a.house) !== normalized(b.house)) return false
  const aName = normalized(a.line_name || a.name)
  const bName = normalized(b.line_name || b.name)
  if (aName === bName) return true
  const shorter = aName.length <= bName.length ? aName : bName
  const longer = shorter === aName ? bName : aName
  // A three-word base is specific enough to fold named flankers into its line
  // (e.g. "The Most Wanted" + "The Most Wanted Intense") without collapsing
  // broad one-word families such as "Wanted", "Wanted Girl", and "Wanted Tonic".
  return shorter.split(' ').length >= 3 && longer.startsWith(`${shorter} `)
}

function concentrationRank(value) {
  const index = CONCENTRATION_ORDER.indexOf(normalized(value))
  return index === -1 ? CONCENTRATION_ORDER.length : index
}

// Collapse exact concentration duplicates and specific named flankers into one
// result card while preserving every selectable catalog row inside `variants`.
export function groupCatalogResults(rows = [], query = '') {
  const exact = new Map()
  rows.forEach((row, index) => {
    const key = `${normalized(row.house)}\u0000${normalized(row.name)}`
    const current = exact.get(key) || {
      line_name: row.name,
      house: row.house,
      variants: [],
      first_index: index,
    }
    current.variants.push(row)
    exact.set(key, current)
  })

  const lines = []
  for (const group of exact.values()) {
    const existing = lines.find(line => sameCatalogLine(line, group))
    if (existing) {
      existing.variants.push(...group.variants)
      existing.first_index = Math.min(existing.first_index, group.first_index)
      if (normalized(group.line_name).length < normalized(existing.line_name).length) {
        existing.line_name = group.line_name
      }
    } else {
      lines.push({ ...group })
    }
  }

  return lines
    .sort((a, b) => a.first_index - b.first_index)
    .map(group => {
      const variants = [...group.variants].sort((a, b) => {
        if (query) {
          const byStrict = Number(strictNameMatch(query, b)) - Number(strictNameMatch(query, a))
          if (byStrict) return byStrict
        }
        const aSuffix = normalized(a.name).slice(normalized(group.line_name).length).trim()
        const bSuffix = normalized(b.name).slice(normalized(group.line_name).length).trim()
        if (aSuffix !== bSuffix) return aSuffix.localeCompare(bSuffix)
        return concentrationRank(resolvedConcentrationDisplay(a))
          - concentrationRank(resolvedConcentrationDisplay(b))
      })
      const representative = variants.find(
        row => normalized(row.name) === normalized(group.line_name),
      ) || variants[0]
      return {
        ...representative,
        name: group.line_name,
        variants,
        variant_count: variants.length,
      }
    })
}
